import type { BlockRegistry } from "../registry/registry.js";
import type { DataSource } from "../types/data.js";
import type { Theme } from "../types/theme.js";
import type { Node, ReportTemplate } from "../types/template.js";
import { CURRENT_SCHEMA_VERSION } from "../types/template.js";
import { parseTemplate } from "../schema/template.schema.js";
import { createId } from "../schema/ids.js";
import { validateTemplate } from "../validate/validate.js";
import type { Issue, IssueCode } from "../validate/issues.js";
import { diffTemplates } from "../diff/template-diff.js";
import { indexTemplate, readRegion, REGION_IDS, type NodeIndex } from "../editor/tree.js";
import { describeBlock } from "./describe.js";
import { bindingSpecsFor } from "../registry/registry.js";
import { assetPlaceholders, restoreAssets } from "./redact.js";
import { REFUSAL_MESSAGE, type TemplateAiRequest, type TemplateAiResult } from "./types.js";

export interface CheckProposalOptions {
  /** The request exactly as it was sent. */
  request: TemplateAiRequest;
  registry: BlockRegistry;
  /** The full data sources. Examples are harmless here: nothing below leaves the process. */
  dataSources: DataSource[];
  theme?: Partial<Theme>;
  /** The design as it stands, with its images — `request.currentTemplate` is the redacted copy. */
  current?: ReportTemplate;
  /**
   * What `redactAssets` took out. Given, the placeholders are put back; absent
   * (a server that only ever saw the redacted copy), they are left in place and
   * the caller who holds the images puts them back.
   */
  assets?: Map<string, string>;
}

export type CheckProposalResult = { ok: true; result: TemplateAiResult } | { ok: false; problems: string[] };

/**
 * Warnings the designer tolerates in a person's own work and does not tolerate
 * in a model's.
 *
 * A person who binds to a field that is not there sees it on the canvas and in
 * the issues list, and meant something by it. A model that does it has made up
 * a field — and the document would print a blank where somebody's figure
 * belongs, with nothing on screen to say why.
 */
const STRICT: ReadonlySet<IssueCode> = new Set<IssueCode>(["unknown-binding", "type-mismatch", "missing-collection", "missing-theme-token", "invalid-condition", "scope-shadowing"]);

const PROBLEM_LIMIT = 30;
/** Top-level parts the model may leave out when it is not changing them. */
const KEEPABLE = ["name", "meta", "page", "styles", "header", "footer", "library", "settings"] as const;
/** A 1×1 PNG: something an image block will accept while a placeholder stands in for the real one. */
const PLACEHOLDER_IMAGE = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

/**
 * Decides whether what a model returned may be shown to a person as a proposal.
 *
 * Model output is untrusted input, so passing the schema is where this starts,
 * not where it ends: the template also has to pass `validateTemplate` against
 * this registry and these data sources, with the warnings above treated as
 * errors — and it may not do what no designer edit could, such as grow props a
 * block does not read, put metadata of its own in `meta.custom`, change the
 * template's identity, or reach outside the person's selection.
 *
 * `problems` is written to be sent back to the model as a repair request.
 */
export function checkProposal(raw: unknown, opts: CheckProposalOptions): CheckProposalResult {
  const output = normalise(raw);
  if (!output) return fail(["Call the tool with an object: { \"outcome\": \"template\", \"template\": {…} }."]);

  if (output.outcome === "refused" || (output.template === undefined && typeof output.reason === "string")) {
    return { ok: true, result: { status: "refused", requestId: opts.request.requestId, reason: typeof output.reason === "string" && output.reason.trim() ? output.reason.trim() : REFUSAL_MESSAGE } };
  }
  // An answer carrying a template is a template, whatever its label says: live
  // runs dropped `outcome` on a repair, or wrote another word there, often
  // enough that refusing a finished design over a label cost a whole attempt.
  // Only "refused" (above) is read from it.
  if (output.outcome !== "template" && !(output.template && typeof output.template === "object")) return fail(['`outcome` must be "template" or "refused".']);
  if (!output.template || typeof output.template !== "object" || Array.isArray(output.template)) return fail(["`template` must be the complete template object."]);

  const problems: string[] = [];
  const candidate = { ...(output.template as Record<string, unknown>) };

  // Parts the model left out because it did not change them are taken from the
  // design as it was sent. Output is where the time and the money go — a design's
  // page settings and text styles are a few thousand tokens of copying — and what
  // is checked below is still a whole template, never a fragment applied to one.
  const current = opts.current;
  const base = (opts.request.currentTemplate ?? current) as Record<string, unknown> | undefined;
  if (base) for (const key of KEEPABLE) if (candidate[key] === undefined && base[key] !== undefined) candidate[key] = structuredClone(base[key]);

  // Identity is not the model's to change: the host stores the design under its id.
  candidate.schemaVersion = CURRENT_SCHEMA_VERSION;
  candidate.id = current?.id ?? (typeof candidate.id === "string" && candidate.id ? candidate.id : createId("tpl"));

  // Images: only the placeholders it was given, and no image data of its own.
  const offered = assetPlaceholders(opts.request.currentTemplate);
  for (const p of assetPlaceholders(candidate)) if (!offered.has(p)) problems.push(`"${p}" is not an image in the design. Use only the asset placeholders you were given.`);
  // A small inline image under the redaction limit was sent as it is and may come back as it is.
  const given = dataUris(opts.request.currentTemplate);
  if ([...dataUris(candidate)].some((u) => !given.has(u))) problems.push("Do not write image data (data: URIs). Keep the asset:N placeholders you were given.");

  wrapLooseBlocks(candidate);

  const parsed = parseTemplate(candidate);
  if (!parsed.ok || !parsed.template) {
    problems.push(...parsed.errors.map((e) => `${e.path || "template"}: ${e.message}`));
    return fail(problems);
  }
  let template = parsed.template;

  // Validate with every image present — real ones where the caller holds them,
  // a stand-in where it does not — and return the template the caller can use.
  const standIns = new Map([...offered].map((p) => [p, opts.assets?.get(p) ?? PLACEHOLDER_IMAGE]));
  const forValidation = restoreAssets(template, standIns).value;
  if (opts.assets) template = forValidation;

  const baseline = current ? new Set(validateTemplate({ template: current, registry: opts.registry, dataSources: opts.dataSources, theme: opts.theme }).issues.map(issueKey)) : new Set<string>();
  const validation = validateTemplate({ template: forValidation, registry: opts.registry, dataSources: opts.dataSources, theme: opts.theme, skipSchema: true });
  for (const i of validation.issues) {
    if (i.severity === "info" || (i.severity === "warning" && !STRICT.has(i.code))) continue;
    // A problem the design already had is the person's to fix, not the model's.
    if (baseline.has(issueKey(i))) continue;
    problems.push(i.nodeId ? `${i.message} (block ${i.nodeId}${i.prop ? `, prop ${i.prop}` : ""})` : i.message);
  }

  problems.push(...unknownProps(template, current, opts.registry));
  problems.push(...bareBindings(template, opts.registry, opts.dataSources));
  problems.push(...metaProblems(template, current));
  if (current && opts.request.selection?.length) {
    // Compare like with like: the design as sent, with the same stand-ins, or an
    // image the model kept untouched would read as a change outside the selection.
    const sent = opts.request.currentTemplate ? restoreAssets(opts.request.currentTemplate, standIns).value : current;
    problems.push(...selectionProblems(sent, forValidation, opts.request.selection, opts.registry));
  }

  if (problems.length) return fail(problems);
  const notes = typeof output.notes === "string" && output.notes.trim() ? output.notes.trim().slice(0, 600) : undefined;
  return { ok: true, result: { status: "proposed", requestId: opts.request.requestId, template, ...(notes ? { notes } : {}) } };
}

/**
 * Blocks placed straight into the body, a header or a footer, wrapped in a section.
 *
 * Every region is a list of sections, and a model asked for a header writes the
 * header's text straight into it about as often as not. A run of loose blocks in
 * a section of its own is exactly what the designer would have made, and it
 * changes nothing about how they look, so it is done here rather than costing a
 * repair — which is a whole second template, and twenty seconds of somebody's
 * wait.
 */
function wrapLooseBlocks(candidate: Record<string, unknown>): void {
  const wrap = (list: unknown): unknown => {
    if (!Array.isArray(list)) return list;
    const out: unknown[] = [];
    let loose: unknown[] = [];
    const flush = () => {
      if (loose.length) out.push({ id: createId("sec"), type: "section", props: { padding: 0 }, children: loose });
      loose = [];
    };
    for (const n of list) {
      if (n && typeof n === "object" && (n as { type?: unknown }).type === "section") {
        flush();
        out.push(n);
      } else loose.push(n);
    }
    flush();
    return out;
  };
  candidate.body = wrap(candidate.body);
  for (const key of ["header", "footer"] as const) {
    const region = candidate[key];
    if (region && typeof region === "object" && !Array.isArray(region)) {
      const r = region as Record<string, unknown>;
      candidate[key] = { ...r, default: wrap(r.default), ...(r.firstPage !== undefined ? { firstPage: wrap(r.firstPage) } : {}) };
    }
  }
}

function fail(problems: string[]): CheckProposalResult {
  const unique = [...new Set(problems)];
  const listed = unique.slice(0, PROBLEM_LIMIT);
  if (unique.length > PROBLEM_LIMIT) listed.push(`…and ${unique.length - PROBLEM_LIMIT} more problems of the same kinds.`);
  return { ok: false, problems: listed };
}

function normalise(raw: unknown): Record<string, unknown> | null {
  let v = raw;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  let out = v as Record<string, unknown>;
  // The tool's own envelope, repeated inside itself: `{ template: { outcome,
  // template: {…} } }`. A live model did this on one plain request in three,
  // and a finished design one level down is still a finished design.
  for (let depth = 0; depth < 3; depth++) {
    // A template sent as a JSON string inside the object — seen on live
    // repairs — is still the template.
    if (typeof out.template === "string") {
      try {
        out = { ...out, template: JSON.parse(out.template) };
      } catch {
        break;
      }
    }
    const inner = out.template as Record<string, unknown> | undefined;
    if (!inner || typeof inner !== "object" || Array.isArray(inner) || "body" in inner) break;
    if (!("template" in inner) && !("outcome" in inner)) break;
    out = { ...inner, notes: inner.notes ?? out.notes };
  }
  return out;
}

function issueKey(i: Issue): string {
  return `${i.code}|${i.nodeId ?? ""}|${i.prop ?? ""}|${i.message}`;
}

function dataUris(v: unknown, out = new Set<string>()): Set<string> {
  if (typeof v === "string") {
    if (/^data:/i.test(v.trim())) out.add(v);
  } else if (Array.isArray(v)) v.forEach((x) => dataUris(x, out));
  else if (v && typeof v === "object") Object.values(v).forEach((x) => dataUris(x, out));
  return out;
}

function allNodes(t: ReportTemplate): Node[] {
  const out: Node[] = [];
  const visit = (nodes: Node[]) => nodes.forEach((n) => (out.push(n), visit(n.children ?? [])));
  for (const r of REGION_IDS) visit(readRegion(t, r));
  return out;
}

/**
 * Props a block does not read.
 *
 * The template schema leaves props open, so this is the only thing standing
 * between a model's invented `aiGenerated: true` and a saved template. A key the
 * node already had is left alone: it is the host's, or an older version's.
 */
function unknownProps(template: ReportTemplate, current: ReportTemplate | undefined, registry: BlockRegistry): string[] {
  const out: string[] = [];
  const known = new Map<string, Set<string>>();
  const before = current ? indexTemplate(current) : new Map() as NodeIndex;
  for (const node of allNodes(template)) {
    const def = registry.get(node.type);
    if (!def) continue;
    let props = known.get(node.type);
    if (!props) known.set(node.type, (props = new Set(describeBlock(def).props)));
    if (props.size === 0) continue;
    const had = before.get(node.id)?.node.props ?? {};
    for (const key of Object.keys(node.props)) if (!props.has(key) && !(key in had)) out.push(`Block ${node.id} (${node.type}) has a prop "${key}" that ${def.name} does not have. Use only its listed props.`);
  }
  return out;
}

/**
 * A template prop whose whole text is a data path: a binding written without its
 * braces. It passes every other check, because it is perfectly good text — and
 * then prints `data.first.invoiceNo` where the invoice number belongs, which is
 * exactly what the first live key/value list did.
 */
function bareBindings(template: ReportTemplate, registry: BlockRegistry, dataSources: DataSource[]): string[] {
  const roots = new Set(dataSources.map((d) => d.id));
  if (!roots.size) return [];
  const looksBound = (v: unknown): v is string => {
    if (typeof v !== "string" || v.includes("{{")) return false;
    const m = /^\s*([A-Za-z_$][\w$]*)(\.[\w$]+)+\s*(\|.*)?$/.exec(v);
    return !!m && roots.has(m[1]!);
  };
  const out: string[] = [];
  for (const node of allNodes(template)) {
    const def = registry.get(node.type);
    if (!def) continue;
    for (const spec of bindingSpecsFor(def)) {
      if (spec.kind === "template" && looksBound(node.props[spec.prop])) out.push(`Block ${node.id}: "${spec.prop}" is text, so "${node.props[spec.prop]}" would print as written. Put the expression in {{ }}.`);
      const list = node.props[spec.prop];
      if (!spec.items || !Array.isArray(list)) continue;
      list.forEach((item, i) => {
        for (const sub of spec.items!) {
          const v = item && typeof item === "object" ? (item as Record<string, unknown>)[sub.prop] : undefined;
          if (sub.kind === "template" && looksBound(v)) out.push(`Block ${node.id}: "${spec.prop}.${i}.${sub.prop}" is text, so "${v}" would print as written. Put the expression in {{ }}.`);
        }
      });
    }
  }
  return out;
}

function metaProblems(template: ReportTemplate, current: ReportTemplate | undefined): string[] {
  const had = new Set(Object.keys(current?.meta?.custom ?? {}));
  const added = Object.keys(template.meta?.custom ?? {}).filter((k) => !had.has(k));
  return added.length ? [`Do not add to meta.custom (${added.join(", ")}). It belongs to the application.`] : [];
}

/**
 * Changes outside what the person selected.
 *
 * The whole template is sent even with a selection, because a block changed in
 * isolation is how a table comes back wider than its column. So the model sees
 * everything and may change only part of it — and this is what holds it to that.
 * The selected blocks may change, go, or gain siblings directly beside them.
 */
function selectionProblems(current: ReportTemplate, proposed: ReportTemplate, selection: string[], registry: BlockRegistry): string[] {
  const selected = new Set(selection);
  const before = indexTemplate(current);
  const after = indexTemplate(proposed);
  const within = (index: NodeIndex, id: string): boolean => {
    let entry = index.get(id);
    while (entry) {
      if (selected.has(entry.node.id)) return true;
      entry = entry.parent ? index.get(entry.parent.id) : undefined;
    }
    return false;
  };
  const beside = new Set<string>();
  for (const id of selection) {
    const e = before.get(id);
    if (e) beside.add(e.parent ? e.parent.id : `region:${e.region}`);
  }
  const out: string[] = [];
  for (const change of diffTemplates(current, proposed, { registry, maxChanges: 1000 }).changes) {
    const id = change.nodeId;
    if (id && (within(before, id) || within(after, id))) continue;
    if (id && change.kind === "added") {
      const e = after.get(id);
      if (e && beside.has(e.parent ? e.parent.id : `region:${e.region}`)) continue;
    }
    out.push(`${change.summary} — that is outside the selected blocks. Undo it; change only the selection.`);
  }
  return out;
}
